import { useState, useMemo, useEffect, useRef } from 'react';
import { TopBar, StageTabs, Stage, QuickFilter, QuickFilterOption } from './TopBar';
import { FilterDrawer, FilterState } from './FilterDrawer';
import { DataTable } from './DataTable';
import { CreditEvaluationTable } from './CreditEvaluationTable';
import { RecordDetailDrawer } from './RecordDetailDrawer';
import { getCustomerStatusAppearance, type CustomerStatusOption } from './TableCells';
import { LeadRecord, afLeads, relationshipManagers, partnerOwners, cities, productTypes } from '../data/mockData';
import { LayoutDashboard, Phone } from 'lucide-react';
import { Tooltip, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import queryString from 'query-string';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { CommentIcon, CustomerDetail, PaginationState, Prospect } from '../../types/prospect';
import ProspectPagination from '../../components/ProspectPagination';
import { format } from 'date-fns';
import AddCustomerPopup from '../../components/AddCustomerPopup';
import CommentPopup from '../../components/CommentPopup';
import { SortField, SortOrder } from './sort';

const initialFilters: FilterState = {
  search: '',
  afLead: '',
  relationshipManager: '',
  partnerOwner: '',
  creditRating: '',
  city: '',
  productType: '',
  dealValueMin: 0,
  priority: '',
  pipelineStatus: '',
  loginStatus: '',
  mraStatus: '',
  tsSignedStatus: '',
  dealType: '',
  companySize: '',
};

const pipelineQuickFilters: QuickFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

const creditEvalQuickFilters: QuickFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'sanctioned', label: 'Sanctioned' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'docs_check', label: 'Signed Agreements' }
];

interface AdminUserList {
  id: number;
  name: string;
};

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

type CustomerApiRecord = Partial<Prospect> & Record<string, unknown>;
type DashboardRecord = LeadRecord & { lenders?: Prospect['lenders'] };

interface PaginationData {
  currentPage: number;
  totalCustomers: number;
  pageSize: number;
  totalPages: number;
}

interface CustomersApiResponse {
  customers: CustomerApiRecord[];
  pagination?: PaginationData;
}

interface StageCountsApiResponse {
  pipelineCount?: number;
  pipeline?: number;
  creditEvaluationCount?: number;
  creditEvaluation?: number;
  activeCustomerCount?: number;
  activePortfolioCount?: number;
  activePortfolio?: number;
  docs_check_count?: number;
  follow_up_count?: {
    customers_count?: number;
  } | number;
}


interface PartnerCompanyList {
  id: number;
  name: string;
}

interface LenderCompanyList {
  id: number;
  name: string;
}

interface LenderUserProductType {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
};


const parseDateValue = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return undefined;
};


const formatFollowUpDateForApi = (value?: Date) => {
  if (!value) {
    return '';
  }

  return format(value, 'yyyy-MM-dd');
};

const normalizeDashboardRecord = (customer: CustomerApiRecord): DashboardRecord => ({
  ...(customer as DashboardRecord),
  next_follow_up_date: parseDateValue(
    customer.next_follow_up_date
    ?? customer.follow_up_date
    ?? customer.nextFollowUp
  ),
});

const getCustomersEndpoint = () => '/customers/fetchCustomersDashboard';
const FOLLOWUPS_COUNT_QUERY_VALUE = 'followupsCount';

const getStageQueryValue = (stage: Stage) => {
  switch (stage) {
    case 'credit-evaluation':
      return 'two';
    case 'active-portfolio':
      return 'three';
    case 'pipeline':
    default:
      return 'one';
  }
};

const getDefaultQuickFilterForStage = (stage: Stage): QuickFilter => {
  if (stage === 'pipeline' || stage === 'credit-evaluation') {
    return 'pending';
  }

  return 'all';
};

const getAdditionalRequestParams = (stage: Stage, quickFilterValue: QuickFilter) => {
  if (stage !== 'pipeline' && stage !== 'credit-evaluation') {
    return {};
  }

  const subCategory = stage === 'credit-evaluation' ? 'credit' : 'pipeline';

  switch (quickFilterValue) {
    case 'pending':
      return {
        type: 'pending',
        subCategory,
      };
    case 'sanctioned':
      return {
        type: 'sanctioned',
        subCategory,
      };
    case 'rejected':
      return {
        type: 'rejected',
        subCategory,
      };
    case 'docs_check':
      return {
        type: 'docs_check',
        subCategory,
      };
    case 'all':
      return {
        type: stage === 'credit-evaluation' ? 'rejected,pending,sanctioned' : 'rejected,pending',
        subCategory,
      };
    default:
      return {
        subCategory,
      };
  }
};

const getStatusSubCategoryForStage = (stage: Stage) => {
  switch (stage) {
    case 'pipeline':
      return 'pipeline';
    case 'credit-evaluation':
      return 'credit';
    default:
      return null;
  }
};

const normalizeCustomerStatusOptions = (payload: unknown): CustomerStatusOption[] => {
  const statusList = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown })?.data)
      ? (payload as { data?: unknown[] }).data ?? []
      : [];

  return statusList
    .map((status) => {
      if (!status || typeof status !== 'object') {
        return null;
      }

      const option = status as Record<string, unknown>;
      const name = typeof option.name === 'string' ? option.name.trim() : '';

      if (!name || (!('id' in option) && !('name' in option))) {
        return null;
      }

      return {
        ...getCustomerStatusAppearance(name),
        id: option.id !== undefined && option.id !== null ? String(option.id) : name,
        name,
        display_order: typeof option.display_order === 'number' ? option.display_order : undefined,
        subCategory: typeof option.sub_category === 'string'
          ? option.sub_category
          : typeof option.subCategory === 'string'
            ? option.subCategory
            : undefined,
        category: typeof option.category === 'string' ? option.category : undefined,
      } satisfies CustomerStatusOption;
    })
    .filter((status): status is CustomerStatusOption => Boolean(status))
    .sort((first, second) => (first.display_order ?? Number.MAX_SAFE_INTEGER) - (second.display_order ?? Number.MAX_SAFE_INTEGER));
};


export default function NewCustomerDashboard() {
  const [activeStage, setActiveStage] = useState<Stage>('pipeline');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(getDefaultQuickFilterForStage('pipeline'));
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    pageSize: 20,
  });
  const [sortField, setSortField] = useState<SortField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(undefined);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    totalCustomers: 0,
    pageSize: 20,
    totalPages: 1,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<LeadRecord | null>(null);
  const [interactionRecord, setInteractionRecord] = useState<LeadRecord | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isInteractionOpen, setIsInteractionOpen] = useState(false);
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [records, setRecords] = useState<LeadRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stageCounts, setStageCounts] = useState({
    pipeline: 0,
    creditEvaluation: 0,
    activePortfolio: 0,
  });
  const [followupsCount, setFollowupsCount] = useState(0);
  const [isFollowupView, setIsFollowupView] = useState(false);
  const shouldApplyFollowupFilter = isFollowupView && activeStage !== 'active-portfolio';
  const [quickFilterCounts, setQuickFilterCounts] = useState<Record<string, number>>({
    all: 0,
    pending: 0,
    rejected: 0,
    sanctioned: 0,
    docs_check: 0,
  });
  const [customerStatusOptions, setCustomerStatusOptions] = useState<CustomerStatusOption[]>([]);
  const [statusUpdateVersion, setStatusUpdateVersion] = useState(0);
  const countCacheRef = useRef(new Map<string, number>());
  const inflightCountRequestsRef = useRef(new Map<string, Promise<number>>());
  const [adminUserList, setAdminUserList] = useState<AdminUserList[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [partnerCompanyList, setPartnerCompanyList] = useState<PartnerCompanyList[]>([]);
  const [lenderCompanyList, setLenderCompanyList] = useState<LenderCompanyList[]>([]);
  const [lenderUserProductTypes, setLenderUserProductType] = useState<LenderUserProductType[]>([]);
  const resetTableSorting = () => {
    setSortField(undefined);
    setSortOrder(undefined);
  };

  const handleTableSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'createdDate' ? 'desc' : 'asc');
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };
  useEffect(() => {
    const fetchCustomerStatusOptions = async () => {
      try {
        const response = await apiService.get<ApiResponse<CustomerStatusOption[]>>('/customers/get-customer-status-by-type');
        setCustomerStatusOptions(normalizeCustomerStatusOptions(response));
      } catch (error) {
        console.error('Error fetching customer status options:', error);
      }
    };

    void fetchCustomerStatusOptions();
  }, []);


  useEffect(() => {
    const fetchAdminUserList = async () => {
      try {
        const response = await apiService.get<{ data: AdminUserList[] }>(`/customers/get-admin-user-list-active`);
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id
        }));
        setAdminUserList(transformedDocs);
      } catch (error) {
        console.error("Error fetching admin users:", error);
      }
    };

    const fetchPartnerCompanyList = async () => {
      try {
        const response = await apiService.get<{ data: PartnerCompanyList[] }>(`/customers/get-partner-company-list`);
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id
        }));
        setPartnerCompanyList(transformedDocs);
      } catch (error) {
        console.error("Error fetching partner companies:", error);
      }
    };

    const fetchLenderCompanyList = async () => {
      try {
        const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id
        }));
        setLenderCompanyList(transformedDocs);
      } catch (error) {
        console.error("Error fetching lender companies:", error);
      }
    };

    const fetchLenderUserProductTypes = async () => {
      try {
        const response = await apiService.get<{ data: LenderUserProductType[] }>(`/lender/get-lender-user-product-types`);
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id,
          display_order: doc.display_order,
          parent_type: doc.parent_type
        }));
        setLenderUserProductType(transformedDocs);
      } catch (error) {
        console.error("Error fetching lender product types:", error);
      }
    };



    fetchLenderCompanyList();
    fetchAdminUserList();
    fetchPartnerCompanyList();
    fetchLenderUserProductTypes();
  }, []);

  const filteredCustomerStatusOptions = useMemo(() => {
    const statusSubCategory = getStatusSubCategoryForStage(activeStage);

    if (!statusSubCategory) {
      return [];
    }

    return customerStatusOptions.filter((status) => status.subCategory === statusSubCategory);
  }, [activeStage, customerStatusOptions]);

  useEffect(() => {
    if (filteredCustomerStatusOptions.length === 0) {
      return;
    }

    setRecords((prev) => prev.map((record) => {
      if (!record.statusId) {
        return record;
      }

      const matchedStatus = filteredCustomerStatusOptions.find((status) => String(status.id) === String(record.statusId));
      if (!matchedStatus || matchedStatus.name === record.status) {
        return record;
      }

      return {
        ...record,
        pipelineStatus: matchedStatus.name,
      };
    }));
  }, [filteredCustomerStatusOptions]);

  useEffect(() => {
    if (activeStage === 'active-portfolio' && filters.pipelineStatus) {
      setFilters((prev) => ({ ...prev, pipelineStatus: '' }));
      return;
    }

    if (!filters.pipelineStatus) {
      return;
    }

    const hasSelectedStatus = filteredCustomerStatusOptions.some(
      (status) => String(status.id) === String(filters.pipelineStatus)
    );

    if (!hasSelectedStatus) {
      setFilters((prev) => ({ ...prev, pipelineStatus: '' }));
    }
  }, [activeStage, filteredCustomerStatusOptions, filters.pipelineStatus]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search]);

  const queryFilters = useMemo(() => ({
    afLead: filters.afLead || undefined,
    relationshipManager: filters.relationshipManager || undefined,
    partnerOwner: filters.partnerOwner || undefined,
    creditRating: filters.creditRating || undefined,
    city: filters.city || undefined,
    productType: filters.productType || undefined,
    dealValueMin: filters.dealValueMin > 0 ? filters.dealValueMin : undefined,
    priority: filters.priority || undefined,
    status: filters.pipelineStatus || undefined,
    pipelineStatus: filters.pipelineStatus || undefined,
    loginStatus: filters.loginStatus || undefined,
    mraStatus: filters.mraStatus === 'Pending'
      ? '0'
      : filters.mraStatus === 'Signed'
        ? '1'
        : filters.mraStatus === 'all'
          ? null
          : undefined,
    termSheetSigned: filters.tsSignedStatus === 'Pending'
      ? '0'
      : filters.tsSignedStatus === 'Signed'
        ? '1'
        : filters.tsSignedStatus === 'all'
          ? null
          : undefined,
    dealType: filters.dealType || undefined,
    companySize: filters.companySize || undefined,
  }), [
    filters.afLead,
    filters.relationshipManager,
    filters.partnerOwner,
    filters.creditRating,
    filters.city,
    filters.productType,
    filters.dealValueMin,
    filters.priority,
    filters.pipelineStatus,
    filters.loginStatus,
    filters.mraStatus,
    filters.tsSignedStatus,
    filters.dealType,
    filters.companySize,
  ]);
  const queryChangeKey = useMemo(
    () => queryString.stringify({
      stage: activeStage,
      quickFilter,
      search: debouncedSearch || undefined,
      pageSize: pagination.pageSize,
      sortBy: sortField || undefined,
      sortOrder: sortOrder || undefined,
      ...queryFilters,
    }),
    [activeStage, debouncedSearch, pagination.pageSize, queryFilters, quickFilter, sortField, sortOrder]
  );
  const previousQueryChangeKeyRef = useRef(queryChangeKey);

  const buildDashboardQuery = (
    stage: Stage,
    quickFilterValue: QuickFilter,
    page: number,
    pageSize: number
  ) => {
    const additionalRequestParams = getAdditionalRequestParams(stage, quickFilterValue);

    return {
      page,
      pageSize,
      stage: getStageQueryValue(stage),
      quickFilter: quickFilterValue !== 'all' ? quickFilterValue : undefined,
      ...additionalRequestParams,
      search: debouncedSearch || undefined,
      ...queryFilters,
      sortBy: sortField || undefined,
      sortOrder: sortOrder || undefined,
      followupData: shouldApplyFollowupFilter ? 'followup' : undefined,
      next_followup: shouldApplyFollowupFilter ? FOLLOWUPS_COUNT_QUERY_VALUE : undefined,
    };
  };

  const extractTotalCustomers = (response: ApiResponse<CustomersApiResponse> | undefined) => {
    if (response?.data?.pagination?.totalCustomers !== undefined) {
      return response.data.pagination.totalCustomers;
    }
    return Array.isArray(response?.data?.customers) ? response.data.customers.length : 0;
  };

  const getCountQuery = (stage: Stage, quickFilterValue: QuickFilter) =>
    buildDashboardQuery(stage, quickFilterValue, 1, 1);

  const getCountQueryKey = (stage: Stage, quickFilterValue: QuickFilter) =>
    queryString.stringify(getCountQuery(stage, quickFilterValue));

  const getCustomerCount = async (stage: Stage, quickFilterValue: QuickFilter) => {
    const queryKey = getCountQueryKey(stage, quickFilterValue);
    const cachedCount = countCacheRef.current.get(queryKey);
    if (cachedCount !== undefined) {
      return cachedCount;
    }

    const inflightRequest = inflightCountRequestsRef.current.get(queryKey);
    if (inflightRequest) {
      return inflightRequest;
    }

    const request = apiService
      .get<ApiResponse<CustomersApiResponse>>(`${getCustomersEndpoint()}?${queryKey}`)
      .then((response) => {
        const totalCustomers = extractTotalCustomers(response);
        countCacheRef.current.set(queryKey, totalCustomers);
        return totalCustomers;
      })

      .finally(() => {
        if (inflightCountRequestsRef.current.get(queryKey) === request) {
          inflightCountRequestsRef.current.delete(queryKey);
        }
      });

    inflightCountRequestsRef.current.set(queryKey, request);
    return request;
  };

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const query = buildDashboardQuery(activeStage, quickFilter, pagination.currentPage, pagination.pageSize);
      const apiUrl = `${getCustomersEndpoint()}?${queryString.stringify(query)}`;
      const countQueryKey = getCountQueryKey(activeStage, quickFilter);
      const responsePromise = apiService.get<ApiResponse<CustomersApiResponse>>(apiUrl);
      const countPromise = responsePromise
        .then((response) => {
          const totalCustomers = extractTotalCustomers(response);
          countCacheRef.current.set(countQueryKey, totalCustomers);
          return totalCustomers;
        })
        .finally(() => {
          if (inflightCountRequestsRef.current.get(countQueryKey) === countPromise) {
            inflightCountRequestsRef.current.delete(countQueryKey);
          }
        });

      inflightCountRequestsRef.current.set(countQueryKey, countPromise);

      const response = await responsePromise;
      const customers = response?.data?.customers;
      const totalCustomers = extractTotalCustomers(response);

      setQuickFilterCounts((prev) => ({
        ...prev,
        [quickFilter]: totalCustomers,
      }));
      setRecords(Array.isArray(customers) ? customers.map(normalizeDashboardRecord) : []);
      setPaginationData(
        response?.data?.pagination ?? {
          currentPage: pagination.currentPage,
          totalCustomers,
          pageSize: pagination.pageSize,
          totalPages: Math.max(
            1,
            Math.ceil(totalCustomers / pagination.pageSize)
          ),
        }
      );
    } catch (error) {
      console.error('Error fetching customers for dashboard:', error);
      toast.error('Failed to fetch customers.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const queryChanged = previousQueryChangeKeyRef.current !== queryChangeKey;
    previousQueryChangeKeyRef.current = queryChangeKey;

    if (queryChanged && pagination.currentPage !== 1) {
      return;
    }



    void fetchCustomers();
  }, [activeStage, debouncedSearch, isFollowupView, pagination.currentPage, pagination.pageSize, queryChangeKey, quickFilter]);

  useEffect(() => {
    const fetchStageCounts = async () => {
      try {
        const countQuery = queryString.stringify({
          next_followup: shouldApplyFollowupFilter ? FOLLOWUPS_COUNT_QUERY_VALUE : undefined,
        });
        const countApiUrl = countQuery
          ? `/customers/customers_count?${countQuery}`
          : '/customers/customers_count';
        const response = await apiService.get<ApiResponse<StageCountsApiResponse>>(countApiUrl);
        const counts = response?.data ?? {};
        const followUpCount = typeof counts.follow_up_count === 'number'
          ? counts.follow_up_count
          : counts.follow_up_count?.customers_count ?? 0;

        const docsCheckCount = counts.docs_check_count;

        setStageCounts({
          pipeline: counts.pipelineCount ?? counts.pipeline ?? 0,
          creditEvaluation: counts.creditEvaluationCount ?? counts.creditEvaluation ?? 0,
          activePortfolio: counts.activeCustomerCount ?? counts.activePortfolioCount ?? counts.activePortfolio ?? 0,
        });
        setFollowupsCount(followUpCount);

        if (docsCheckCount !== undefined) {
          setQuickFilterCounts((prev) => ({
            ...prev,
            docs_check: docsCheckCount,
          }));
        }
      } catch (error) {
        console.error('Error fetching stage counts:', error);
      }
    };

    if (statusUpdateVersion === 0) {
      // Initial page load — call immediately
      void fetchStageCounts();
    } else {
      // After an update — wait 1s for DB to commit before fetching count
      const timer = setTimeout(() => {
        void fetchStageCounts();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldApplyFollowupFilter, statusUpdateVersion]);

  const filterOptions = useMemo(() => ({
    afLeads,
    relationshipManagers,
    partnerOwners,
    cities,
    productTypes
  }), []);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === 'search') return false;
      if (key === 'dealValueMin') return (value as number) > 0;
      return value !== '' && value !== 'all';
    }).length;
  }, [filters]);

  const currentQuickFilterOptions = useMemo(() => {
    if (activeStage === 'credit-evaluation') return creditEvalQuickFilters;
    if (activeStage === 'active-portfolio') return [];
    return pipelineQuickFilters;
  }, [activeStage]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [activeStage, debouncedSearch, queryFilters, quickFilter]);

  useEffect(() => {
    setPagination((prev) => (
      prev.currentPage > paginationData.totalPages
        ? { ...prev, currentPage: paginationData.totalPages }
        : prev
    ));
  }, [paginationData.totalPages]);

  useEffect(() => {
    const fetchQuickFilterCounts = async () => {
      try {
        const entries = await Promise.all(
          currentQuickFilterOptions.map(async (option) => {
            const totalCustomers = await getCustomerCount(activeStage, option.value as QuickFilter);
            return [option.value, totalCustomers] as const;
          })
        );

        setQuickFilterCounts(Object.fromEntries(entries));
      } catch (error) {
        console.error('Error fetching quick filter counts:', error);
      }
    };

    void fetchQuickFilterCounts();
  }, [activeStage, currentQuickFilterOptions, shouldApplyFollowupFilter, statusUpdateVersion]);

  const currentPageStart = paginationData.totalCustomers === 0
    ? 0
    : ((paginationData.currentPage - 1) * paginationData.pageSize) + 1;
  const currentPageEnd = paginationData.totalCustomers === 0
    ? 0
    : currentPageStart + records.length - 1;

  const handleRowClick = (record: LeadRecord) => {
    setSelectedRecord(record);
    setIsDetailOpen(true);
    // setSelectedCustomerId(record.id);
  };

  const handleInteractionClick = (record: LeadRecord) => {
    setSelectedCustomerId(record.id);
    setInteractionRecord(record);
    setIsInteractionOpen(true);
  };


  const handleUpdateRecord = async (id: string, updates: Partial<LeadRecord>) => {
    const currentRecord = records.find((record) => record.id === id);
    const currentNextFollowupTime = parseDateValue(currentRecord?.next_follow_up_date)?.getTime();
    const updatedNextFollowupTime = parseDateValue(updates.next_follow_up_date)?.getTime();
    const isStatusUpdate = updates.statusId !== undefined && updates.statusId !== currentRecord?.statusId;
    const isPriorityUpdate = updates.pipeline !== undefined && updates.pipeline !== currentRecord?.pipeline;
    const isNextFollowupUpdate = Object.prototype.hasOwnProperty.call(updates, 'next_follow_up_date')
      && currentNextFollowupTime !== updatedNextFollowupTime;

    const applyRecordUpdates = (partialUpdates: Partial<LeadRecord>) => {
      setRecords((prev) => prev.map((record) =>
        record.id === id ? { ...record, ...partialUpdates } : record
      ));
      setSelectedRecord((prev) => (
        prev && prev.id === id ? { ...prev, ...partialUpdates } : prev
      ));
      setInteractionRecord((prev) => (
        prev && prev.id === id ? { ...prev, ...partialUpdates } : prev
      ));
    };

    if (isNextFollowupUpdate) {
      applyRecordUpdates(updates);
      try {
        await apiService.put<ApiResponse<unknown>>('/customers/update-customer-nextfollowupdate', {
          customer_id: id,
          follow_up_date: formatFollowUpDateForApi(updates.next_follow_up_date),
        });

        countCacheRef.current.clear();
        inflightCountRequestsRef.current.clear();
        setStatusUpdateVersion((prev) => prev + 1);
      } catch (error) {
        console.error('Error updating customer next follow up date:', error);
        toast.error('Failed to update next followup date.');
      }

      return;
    }

    if (isPriorityUpdate) {
      applyRecordUpdates(updates);
      try {
        await apiService.put<ApiResponse<unknown>>('/customers/update-customer-priority', {
          customer_id: id,
          priority: updates.pipeline,
        });

        countCacheRef.current.clear();
        inflightCountRequestsRef.current.clear();
        setStatusUpdateVersion((prev) => prev + 1);
      } catch (error) {
        console.error('Error updating customer priority:', error);
        toast.error('Failed to update customer priority.');
      }

      return;
    }

    if (isStatusUpdate) {
      applyRecordUpdates(updates);
      try {
        await apiService.put<ApiResponse<unknown>>('/customers/update-customer-status', {
          status_id: updates.statusId,
          customer_id: id,
        });

        countCacheRef.current.clear();
        inflightCountRequestsRef.current.clear();
        setStatusUpdateVersion((prev) => prev + 1);
      } catch (error) {
        console.error('Error updating customer status:', error);
        toast.error('Failed to update customer status.');
      }

      return;
    }

    applyRecordUpdates(updates);
  };

  const handleSearchChange = (search: string) => {
    setFilters((prev) => ({ ...prev, search }));
  };

  const handlePaginationChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleClearFilters = () => {
    setFilters({ ...initialFilters });
    setQuickFilter(getDefaultQuickFilterForStage(activeStage));
  };

  const handleStageChange = (stage: Stage) => {
    setActiveStage(stage);
    setQuickFilter(getDefaultQuickFilterForStage(stage));
    resetTableSorting();
  };

  const handleQuickFilterChange = (filter: QuickFilter) => {
    setQuickFilter(filter);
    resetTableSorting();
  };

  const handleFollowupsClick = () => {
    setIsFollowupView((prev) => !prev);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleApiCall = async (apiCall: () => Promise<any>, successMessage?: string) => {
    try {
      const response = await apiCall();
      // apiService returns response.data directly, so a resolved promise is success.
      if (successMessage) {
        toast.success(successMessage);
      }
      return response;
    } catch (error: any) {
      const apiErrorMessage =
        (typeof error?.message === 'string' && error.message)
        || (typeof error?.error === 'string' && error.error)
        || (typeof error?.data?.message === 'string' && error.data.message)
        || (typeof error?.response?.data?.message === 'string' && error.response.data.message)
        || "An error occurred. Please try again.";

      console.error("API Error:", apiErrorMessage);
      toast.error(apiErrorMessage);
      throw error;
    }
  };

  const handleAddCustomerSubmit = async (customerData: Partial<CustomerDetail>) => {
    if (!customerData?.email) return false;
    if (!customerData?.name) return false;
    try {
      const successMessage = customerData.id
        ? "Customer updated successfully!"
        : "Customer created successfully!";

      await handleApiCall(
        () =>
          apiService.post(`/customers/create-customer`, {
            customerData
          }),
        successMessage
      );

      const countQuery = queryString.stringify({
        next_followup: shouldApplyFollowupFilter ? FOLLOWUPS_COUNT_QUERY_VALUE : undefined,
      });
      const countApiUrl = countQuery
        ? `/customers/customers_count?${countQuery}`
        : '/customers/customers_count';
      const response = await apiService.get<ApiResponse<StageCountsApiResponse>>(countApiUrl);
      const counts = response?.data ?? {};
      const followUpCount = typeof counts.follow_up_count === 'number'
        ? counts.follow_up_count
        : counts.follow_up_count?.customers_count ?? 0;

      const docsCheckCount = counts.docs_check_count;

      setStageCounts({
        pipeline: counts.pipelineCount ?? counts.pipeline ?? 0,
        creditEvaluation: counts.creditEvaluationCount ?? counts.creditEvaluation ?? 0,
        activePortfolio: counts.activeCustomerCount ?? counts.activePortfolioCount ?? counts.activePortfolio ?? 0,
      });
      setFollowupsCount(followUpCount);

      if (docsCheckCount !== undefined) {
        setQuickFilterCounts((prev) => ({
          ...prev,
          docs_check: docsCheckCount,
        }));
      }

      // Clear any cached data
      // setProspects([]); // Clear existing data
      setPaginationData({
        currentPage: 1,
        totalCustomers: 0,
        pageSize: 20,
        totalPages: 1
      });

      // Reset filters to ensure fresh data fetch
      // setCurrentFilter(prev => ({
      //   ...prev,
      //   page: 1
      // }));

      // Force a fresh data fetch
      // await Promise.all([
      //   fetchPageDataWithFilters(),
      //   fetchStatusCounts()
      // ]);

      // Close the modal if needed
      setIsAddLeadOpen(false);

      void fetchCustomers();
      return true; // Return true on success
    } catch (error) {
      console.error("Error creating/updating customer:", error);
      return false; // Return false on failure
    }
  };

  const handleSaveComment = async (commentText: string, mentions?: string[], icon?: CommentIcon) => {
    const id = selectedCustomerId;
    const type = "prospect";

    try {
      // Convert mention IDs to names for display
      const mentionNames = mentions?.map(mentionId => {
        const admin = adminUserList.find(user => String(user.id) === String(mentionId));
        return admin?.name || mentionId;
      });

      // Send both IDs and names to the backend
      await handleApiCall(
        () =>
          apiService.post(`/customers/create-comment`, {
            commentText,
            mentions, // Original IDs for backend processing
            mentionNames, // Names for display
            icon,
            id,
            type,
          }),
        "Comment added successfully!"
      );

      // Create a new comment object for the UI
      const newComment: Comment = {
        id: Date.now().toString(),
        text: commentText,
        author: JSON.parse(localStorage.getItem("detail") || '{}')?.name || "You", // Use the actual user name
        date: new Date().toISOString(),
        mentions: mentionNames, // Use names for display
        icon,
        resolutionStatus: 'pending'
      };

      // Update the UI with the new comment
      const updatedProspects = records.map((prospect: any) => {
        if (type === "prospect" && prospect.id === id) {
          return {
            ...prospect,
            comments: [...(prospect.comments || []), newComment],
            lastUpdated: new Date().toISOString(),
          };
        }
        //  else if (type === "lender") {
        //   const updatedLenders = prospect.lenders.map((lender) => {
        //     if (lender.id === id) {
        //       return {
        //         ...lender,
        //         comments: [...(lender.comments || []), newComment],
        //         lastUpdated: new Date().toISOString(),
        //       };
        //     }
        //     return lender;
        //   });
        //   return {
        //     ...prospect,
        //     lenders: updatedLenders,
        //   };
        // }
        return prospect;
      });

      setRecords(updatedProspects);
    } catch (error) {
      console.error("Error saving comment:", error);
    }
  };

  const handleResolveComment = async (commentId: string) => {
    const id = selectedCustomerId;
    const type = "prospect";

    try {
      // Make API call to resolve the comment
      const response = await handleApiCall(
        () =>
          apiService.post(`/customers/mark-comment-resolved`, {
            commentId,
            id, // prospect or lender ID
            type // "prospect" or "lender"
          }),
        "Comment resolved successfully!"
      );

      return response; // Return the response so the CommentPopup knows the API call was successful
    } catch (error) {
      console.error("Error resolving comment:", error);
      throw error; // Rethrow the error so the CommentPopup knows the API call failed
    }
  };


  const getActiveComments = (): any[] => {
    // const { id , type } = selectedCustomerId ? { id: selectedCustomerId, type: "prospect" } : { id: "", type: "" };
    const id = selectedCustomerId;
    const type = "prospect";
    if (type === "prospect") {
      const prospect = records.find(p => p.id === id);
      return prospect?.comments || [];
    } else {
      // for (const prospect of prospects) {
      //   const lender = prospect.lenders.find(l => l.id === id);
      //   if (lender) {
      //     return lender.comments || [];
      //   }
      // }
    }
    return [];
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-[#f8fafb] w-[100%] overflow-hidden">
      {/* <div className="customer-dashboard-zoom-shell"> */}
      {/* <div className="customer-dashboard-zoom-content"> */}
      <header className="shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1920px] mx-auto px-5 py-2.5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-[#eef7f8] rounded-2xl">
                <LayoutDashboard className="w-4.5 h-4.5 text-[#5da8b4]" />
              </div>
              <div>
                <h1 className="text-[20px] leading-none font-semibold text-slate-800 tracking-tight">Customer Dashboard</h1>
                <p className="mt-1 text-[11px] text-slate-500">Pipeline & portfolio management</p>
              </div>
            </div>

            <div className="flex justify-center xl:flex-1">
              <StageTabs
                activeStage={activeStage}
                onStageChange={handleStageChange}
                counts={stageCounts} />
            </div>

            <div className="flex items-center">
              {activeStage !== 'active-portfolio' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={`relative p-2 rounded-lg transition-colors focus-ring ${shouldApplyFollowupFilter
                          ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                          : 'hover:bg-muted text-muted-foreground'
                          }`}
                        onClick={handleFollowupsClick}
                        aria-pressed={shouldApplyFollowupFilter}
                      >
                        <Phone className={`w-4 h-4 ${shouldApplyFollowupFilter ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                        {followupsCount > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-rose-500 text-white ring-2 ring-white">
                            {followupsCount}
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="shrink-0 bg-white border-b border-slate-200">
        <div className="max-w-[1920px] mx-auto px-5 py-3 bg-white">
          <TopBar
            search={filters.search}
            onSearchChange={handleSearchChange}
            quickFilter={quickFilter}
            onQuickFilterChange={handleQuickFilterChange}
            quickFilterOptions={currentQuickFilterOptions}
            quickFilterCounts={quickFilterCounts}
            activeFilterCount={activeFilterCount}
            onOpenFilters={() => setIsFilterOpen(true)}
            onAddLead={() => setIsAddLeadOpen(true)}
            onClearFilters={handleClearFilters} />
        </div>
      </div>

      <main className="flex-1 min-h-0">
        <div className="max-w-[1920px] mx-auto h-full px-5 py-3">
          <div className="relative z-0 flex h-full min-h-0 flex-col gap-4">
            <div className="min-h-0 flex-1">
              {isLoading ? (
                <div className="flex min-h-[320px] items-center justify-center rounded-[24px] border border-slate-200 bg-white text-sm text-muted-foreground shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                  Loading customers...
                </div>
              ) : activeStage === 'credit-evaluation' ? (
                <CreditEvaluationTable
                  data={records}
                  statusOptions={filteredCustomerStatusOptions}
                  onRowClick={handleRowClick}
                  onInteractionClick={handleInteractionClick}
                  onUpdateRecord={handleUpdateRecord}
                  adminUserList={adminUserList}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSortChange={handleTableSort}
                />
              ) : (
                <DataTable
                  data={records}
                  activeStage={activeStage}
                  statusOptions={filteredCustomerStatusOptions}
                  onRowClick={handleRowClick}
                  onInteractionClick={handleInteractionClick}
                  onUpdateRecord={handleUpdateRecord}
                  adminUserList={adminUserList}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSortChange={handleTableSort}
                />
              )}
            </div>

            {paginationData.totalCustomers > 0 && (
              <div className="flex flex-col gap-3 rounded-[20px] border border-slate-200 bg-white px-4 py-3 shadow-[0_10px_30px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {currentPageStart}-{currentPageEnd} of {paginationData.totalCustomers} records
                </div>
                {paginationData.totalPages > 1 && (
                  <ProspectPagination
                    totalItems={paginationData.totalCustomers}
                    pagination={pagination}
                    onPaginationChange={handlePaginationChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>


      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFilterChange={setFilters}
        filterOptions={filterOptions}
        activeStage={activeStage}
        statusOptions={filteredCustomerStatusOptions.map((status) => ({
          id: status.id,
          name: status.name,
        }))}
      />

      <RecordDetailDrawer
        record={selectedRecord}
        isOpen={isDetailOpen}
        onClose={() => { setIsDetailOpen(false); setSelectedRecord(null); }}
        onUpdateRecord={handleUpdateRecord}
        adminUserList={adminUserList}
      />

      {/* Comment popup */}
      <CommentPopup
        isOpen={isInteractionOpen}
        comments={getActiveComments()}
        onSave={handleSaveComment}
        onResolve={handleResolveComment}
        onClose={() => { setIsInteractionOpen(false); setSelectedCustomerId(null); }}
        relationshipManagers={adminUserList}
        currentUser={JSON.parse(localStorage.getItem("detail") || '{}')?.name || "You"}
      />

      {/* <InteractionDrawer
        record={interactionRecord}
        isOpen={isInteractionOpen}
        onClose={() => { setIsInteractionOpen(false); setInteractionRecord(null); }} /> */}

      {/* Tag Lender Popup */}
      <AddCustomerPopup
        admin_users={adminUserList}
        isOpen={isAddLeadOpen}
        lender_user_product_types={lenderUserProductTypes}
        onClose={() => {
          setIsAddLeadOpen(false);
        }}
        onSubmit={handleAddCustomerSubmit}
        isEdit={false}
        customerId={null}
      />
    </div>
  );
}
